import { AUTH0_REAUTH_QUERY_PARAM, getPathnameFromPath, NAVIGATION_URLS } from '@libs/core';

import { getAuth0RedirectOrigin } from './getAuth0RedirectOrigin';
import { sanitizeSegmentUrl } from './sanitize';

const returnToExcludedPathnames = new Set([
    NAVIGATION_URLS.velocity.signIn,
    NAVIGATION_URLS.velocity.createAccount,
    '/auth/callback',
]);

export type GetAuth0ReturnToInput = {
    /** Full path, optionally including a query string. */
    path?: string | null;
    /** Pathname when path and search are supplied separately. */
    pathname?: string | null;
    /** Query string to append to pathname (with or without a leading `?`). */
    search?: string | null;
};

type Auth0ReturnToSearchParams = {
    get(name: string): string | null;
};

export type ResolveAuth0ReturnToInput = {
    searchParams?: Auth0ReturnToSearchParams | null;
    defaultReturnTo: string;
    /** Sanitization base. Defaults to `getAuth0RedirectOrigin()`. */
    origin?: string;
};

/**
 * Builds the `returnTo` used when inactivity logs the user out: back to the sign-in page,
 * carrying where to resume afterwards.
 *
 * Auth0's `/v2/logout` ignores the query string when validating `returnTo` against
 * Allowed Logout URLs, so only `${origin}/sign-in` has to be registered.
 */
export function buildAuth0ReauthSignInUrl(origin: string, destination: string): string {
    const params = new URLSearchParams({
        redirectUrl: destination,
        [AUTH0_REAUTH_QUERY_PARAM]: '1',
    });

    return `${origin}${NAVIGATION_URLS.velocity.signIn}?${params.toString()}`;
}

/**
 * Removes trailing `/` characters from a path.
 *
 * Scans backwards instead of matching `/\/+$/`, whose backtracking is quadratic on
 * slash-only input and therefore a denial-of-service vector for attacker-supplied paths.
 */
export function stripTrailingSlashes(value?: string | null): string {
    if (typeof value !== 'string' || value.length === 0) {
        return '';
    }

    let end = value.length;

    while (end > 0 && value[end - 1] === '/') {
        end -= 1;
    }

    return end === value.length ? value : value.slice(0, end);
}

export function getAuth0ReturnTo(input: GetAuth0ReturnToInput = {}): string {
    const { path, pathname, search } = input;

    let resolvedPath: string | null | undefined;

    if (path !== undefined) {
        resolvedPath = path;
    } else if (pathname) {
        if (search) {
            const querySuffix = search.startsWith('?') ? search : `?${search}`;
            resolvedPath = `${pathname}${querySuffix}`;
        } else {
            resolvedPath = pathname;
        }
    }

    if (!resolvedPath) {
        return '/';
    }

    const normalizedPathname = stripTrailingSlashes(getPathnameFromPath(resolvedPath)) || '/';

    if (returnToExcludedPathnames.has(normalizedPathname)) {
        return '/';
    }

    return resolvedPath;
}

/**
 * Reads `returnTo` (or the legacy `redirectUrl`) from the current query string,
 * sanitizes it to a same-app path, and collapses recursive Auth0 destinations
 * (`/sign-in`, `/create-account`, `/auth/callback`) to `/`.
 */
export function resolveAuth0ReturnTo({
    searchParams,
    defaultReturnTo,
    origin = getAuth0RedirectOrigin(),
}: ResolveAuth0ReturnToInput): string {
    const rawReturnTo = searchParams?.get('returnTo') ?? searchParams?.get('redirectUrl') ?? defaultReturnTo;
    const safeReturnTo = sanitizeSegmentUrl(rawReturnTo, origin, { includeSearch: true });

    return getAuth0ReturnTo({ path: safeReturnTo });
}
